<template>
  <div
    ref="barChart"
    class="bar-chart-container" />
</template>

<script>
  import * as echarts from 'echarts'

  export default {
    name: 'BarChart',
    inject: {
      isContained: {
        default: () => true,
      },
    },
    props: {
      data: {
        type: Array,
        required: true,
      },
      yAxisDataType: {
        type: String,
        required: true,
      },
      yAxisDataTypes: {
        type: Array,
        default: () => [],
      },
      yAxisDataFormats: {
        type: Array,
        default: () => [],
      },
      measureLabel: {
        type: String,
        default: '',
      },
      measureLabels: {
        type: Array,
        default: () => [],
      },
      benchmarkLabel: {
        type: String,
        default: '',
      },
      benchmarkLabels: {
        type: Array,
        default: () => [],
      },
      avgBenchmark: {
        type: Object,
        default: null,
      },
    },
    data () {
      return {
        chartInstance: null,
      }
    },
    computed: {
      isContainerExpanded () {
        return this.isContained()
      },

      isGrouped () {
        return this.data.length > 0 && Array.isArray(this.data[0]?.bars)
      },

      measureCount () {
        if (!this.isGrouped) return 1
        return this.data[0]?.bars?.length || 1
      },

      // Scale a value for display (percentages stored as decimals, need * 100)
      scaleValue () {
        return (value) => this.yAxisDataType === 'percentage'
          ? value * 100
          : value
      },

      // Peak absolute value per measure across all dimension groups
      measureMaxValues () {
        if (!this.isGrouped) return {}
        let maxByMeasure = {}
        for (let group of this.data) {
          for (let measureIdx = 0; measureIdx < group.bars.length; measureIdx++) {
            let barValue = Math.abs(group.bars[measureIdx]?.value || 0)
            if (maxByMeasure[measureIdx] === undefined || barValue > maxByMeasure[measureIdx]) {
              maxByMeasure[measureIdx] = barValue
            }
          }
        }
        return maxByMeasure
      },

      // Assigns each measure to axis 0 (left) or 1 (right)
      // Splits by data type first, then by scale (5x ratio)
      axisAssignments () {
        let measureIndices = Object.keys(this.measureMaxValues).map(Number).sort()
        if (measureIndices.length <= 1) {
          return {}
        }

        // Different data types — split by type (first type = left, others = right)
        let hasTypeDifference = this.yAxisDataTypes.length > 1
          && new Set(this.yAxisDataTypes).size > 1

        if (hasTypeDifference) {
          let firstType = this.yAxisDataTypes[0]
          let assignments = {}
          for (let idx of measureIndices) {
            assignments[idx] = this.yAxisDataTypes[idx] === firstType ? 0 : 1
          }
          return assignments
        }

        // Same data type — split by scale if ratio > 5x
        // First measure anchors left axis, others compared against it
        let firstMax = this.measureMaxValues[measureIndices[0]] || 1
        let assignments = {}
        for (let idx of measureIndices) {
          let bigger = Math.max(firstMax, this.measureMaxValues[idx] || 1)
          let smaller = Math.max(Math.min(firstMax, this.measureMaxValues[idx] || 1), 0.001)
          assignments[idx] = (bigger / smaller) > 5 ? 1 : 0
        }
        return assignments
      },

      // True when measures are split across two axes
      hasDualAxis () {
        let values = Object.values(this.axisAssignments)
        return values.includes(0) && values.includes(1)
      },

      // Maps each axis (0=left, 1=right) to its data type for label formatting
      axisDataTypes () {
        let leftType = this.yAxisDataTypes[0] || this.yAxisDataType
        let rightType = leftType
        for (let [idx, axis,] of Object.entries(this.axisAssignments)) {
          if (axis === 1) {
            rightType = this.yAxisDataTypes[idx] || this.yAxisDataType
          }
        }
        return [leftType, rightType,]
      },

      // Format a value based on its data type and optional DataFormat config
      formatValueForType () {
        return (dataType, value, dataFormat) => {
          if (value === 0) return ''
          if (dataType === 'money') {
            return '$' + (value / 100).toLocaleString()
          }
          if (dataType === 'percentage') {
            return value.toFixed(2) + '%'
          }
          if (dataType === 'duration' && (dataFormat?.mm_ss || dataFormat?.show_duration_label)) {
            let sign = value < 0 ? '-' : ''
            let abs = Math.abs(value)
            let hours = Math.floor(abs)
            let minutes = Math.floor((abs * 60) % 60)
            let seconds = Math.floor((abs * 3600) % 60)
            if (hours > 0) {
              return `${sign}${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
            }
            return `${sign}${minutes}:${String(seconds).padStart(2, '0')}`
          }
          return value
        }
      },

      // Maps each axis to its DataFormat config
      axisDataFormats () {
        let leftFormat = this.yAxisDataFormats[0] || null
        let rightFormat = leftFormat
        for (let [idx, axis,] of Object.entries(this.axisAssignments)) {
          if (axis === 1) {
            rightFormat = this.yAxisDataFormats[idx] || null
          }
        }
        return [leftFormat, rightFormat,]
      },

      // ECharts yAxis config — single object for one axis, array for dual
      yAxisConfig () {
        if (!this.hasDualAxis) {
          return {
            type: 'value',
            axisLabel: {
              color: '#B2B1B6',
              formatter: (value) => this.formatValueForType(this.axisDataTypes[0], value, this.axisDataFormats[0]),
            },
          }
        }
        // Dual axis — left and right with independent scales
        return this.axisDataTypes.map((dataType, idx) => ({
          type: 'value',
          position: idx === 0 ? 'left' : 'right',
          axisLabel: {
            color: '#B2B1B6',
            formatter: (value) => this.formatValueForType(dataType, value, this.axisDataFormats[idx]),
          },
          splitLine: {
            show: idx === 0,
          },
        }))
      },

      chartOptions () {
        const hasAverage = !!(this.avgBenchmark && this.avgBenchmark.value)

        if (this.isGrouped && this.measureCount > 1) {
          return this.multiMeasureOptions(hasAverage)
        }
        return this.singleMeasureOptions(hasAverage)
      },
    },
    watch: {
      data: {
        handler () {
          this.updateChart()
        },
        deep: true,
      },
      isContainerExpanded: {
        handler () {
          this.$nextTick(() => {
            this.resizeChart()
          })
        },
      },
    },
    mounted () {
      this.initChart()
      window.addEventListener('resize', this.resizeChart)
    },
    beforeDestroy () {
      if (this.chartInstance) {
        this.chartInstance.dispose()
      }
      window.removeEventListener('resize', this.resizeChart)
    },
    methods: {
      singleMeasureOptions (hasAverage) {
        const items = this.isGrouped
          ? this.data.map((group) => ({
            ...group.bars[0],
            color: group.color,
            label: group.label,
          }))
          : this.data

        const series = [
          {
            name: this.measureLabel,
            type: 'bar',
            data: items.map((item) => {
              const qualityColor =
                item.conditional_quality === 'good'
                  ? '#008C84'
                  : item.conditional_quality === 'bad'
                    ? '#d31b5e'
                    : null

              return {
                value: this.scaleValue(item.value),
                formatted: item.formatted_value,
                itemStyle: {
                  color: qualityColor
                    || (hasAverage
                      ? (item.value >= this.avgBenchmark.value ? '#008C84' : '#d31b5e')
                      : item.color),
                },
              }
            }),
            barMaxWidth: '50%',
            itemStyle: {
              borderRadius: [3, 3, 0, 0,],
            },
            z: 2,
            silent: true,
          },
        ]

        if (hasAverage) {
          series.push({
            name: 'Average Benchmark',
            type: 'line',
            markLine: {
              silent: true,
              symbol: 'none',
              label: {
                show: true,
                position: 'insideEndTop',
                formatter: this.avgBenchmark.label,
                overflow: 'break',
                padding: [6, 8, 6, 8,],
                backgroundColor: '#10141BBF',
                color: '#fff',
                borderRadius: 4,
                fontSize: 11,
              },
              data: [{
                yAxis: this.scaleValue(this.avgBenchmark.value),
                lineStyle: {
                  color: '#87898D',
                  width: 2,
                  shadowColor: '#fff',
                  shadowBlur: 2,
                  type: 'solid',
                },
              },],
            },
            data: [],
          })
        } else {
          series.push({
            name: this.benchmarkLabel,
            type: 'bar',
            data: items.map((item) => ({
              value: this.scaleValue(item.benchmark),
              formatted: item.formatted_benchmark,
              itemStyle: {
                color: item.color + '33',
              },
            })),
            barMaxWidth: '50%',
            barGap: '-40%',
            itemStyle: {
              borderRadius: [5, 5, 0, 0,],
            },
            z: 1,
            silent: true,
          })
        }

        return this.buildChartConfig(
          items.map((item) => item.label),
          series
        )
      },

      multiMeasureOptions () {
        const labels = this.data.map((group) => group.label)
        const series = []

        for (let measureIdx = 0; measureIdx < this.measureCount; measureIdx++) {
          const measureLabel = this.measureLabels[measureIdx] || `Measure ${measureIdx + 1}`
          const benchmarkLabel = this.benchmarkLabels[measureIdx] || 'Benchmark'
          const measureOpacity = Math.max(0.4, 1.0 - (measureIdx * 0.2))

          const measureSeries = {
            name: measureLabel,
            type: 'bar',
            data: this.data.map((group) => {
              const bar = group.bars?.[measureIdx]
              return {
                value: bar ? this.scaleValue(bar.value) : null,
                formatted: bar?.formatted_value,
                itemStyle: {
                  color: group.color,
                  opacity: measureOpacity,
                },
              }
            }),
            itemStyle: {
              borderRadius: [3, 3, 0, 0,],
            },
            z: 2,
            silent: true,
          }
          const benchmarkSeries = {
            name: benchmarkLabel,
            type: 'bar',
            data: this.data.map((group) => {
              const bar = group.bars?.[measureIdx]
              return {
                value: bar ? this.scaleValue(bar.benchmark) : null,
                formatted: bar?.formatted_benchmark,
                itemStyle: {
                  color: group.color + '33',
                  opacity: measureOpacity,
                },
              }
            }),
            itemStyle: {
              borderRadius: [5, 5, 0, 0,],
            },
            z: 1,
            silent: true,
          }
          // Bind each measure to its assigned y-axis (0=left, 1=right)
          // Right-axis bars get a pattern fill to visually distinguish scale
          if (this.hasDualAxis) {
            let axisIdx = this.axisAssignments[measureIdx] || 0
            measureSeries.yAxisIndex = axisIdx
            benchmarkSeries.yAxisIndex = axisIdx
            if (axisIdx === 1) {
              measureSeries.itemStyle.decal = {
                symbol: 'rect',
                symbolSize: 1,
                rotation: Math.PI / 4,
                dashArrayX: [1, 0,],
                dashArrayY: [4, 3,],
                color: 'rgba(0, 0, 0, 0.15)',
              }
            }
          }
          series.push(measureSeries)
          series.push(benchmarkSeries)
        }

        return this.buildChartConfig(labels, series)
      },

      buildChartConfig (xAxisLabels, series) {
        return {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow',
              snap: true,
            },
            backgroundColor: '#28272A',
            borderColor: '#333333',
            textStyle: {
              color: '#FFFFFF',
              fontSize: 12,
            },
            formatter: (params) => {
              let result = `<div style="font-size:12px;text-transform:capitalize">${params[0].name}</div>`

              params.forEach((param) => {
                const value = param.data.formatted || param.value
                result += `<div style="display:flex;justify-content:space-between;gap:8px;margin-top:4px">
                <span>${param.marker}${param.seriesName}:</span>
                <span style="">${value}</span>
              </div>`
              })

              result +=
                '<div style="font-size:12px;color:#888;margin-top:8px;border-top:1px solid #555; padding-top: 10px;">Click to drill down</div>'
              return result
            },
          },
          grid: {
            left: '3%',
            right: '4%',
            bottom: '3%',
            top: '3%',
            containLabel: true,
          },
          xAxis: {
            type: 'category',
            data: xAxisLabels,
            axisLabel: {
              interval: 0,
              rotate: 45,
              margin: 15,
              color: '#7E7D84',
              hideOverlap: true,
            },
            axisLine: {
              show: false,
            },
            axisTick: {
              show: false,
            },
          },
          yAxis: this.yAxisConfig,
          series,
        }
      },

      initChart () {
        this.chartInstance = echarts.init(this.$refs.barChart)
        this.chartInstance.setOption(this.chartOptions)

        this.$refs.barChart.addEventListener('click', (event) => {
          const rect = this.$refs.barChart.getBoundingClientRect()
          const x = event.clientX - rect.left
          const y = event.clientY - rect.top

          const pointInPixel = [x, y,]
          const pointInGrid = this.chartInstance.convertFromPixel(
            { seriesIndex: 0, },
            pointInPixel
          )

          if (pointInGrid) {
            const dataIndex = Math.round(pointInGrid[0])
            if (dataIndex >= 0 && dataIndex < this.data.length) {
              const item = this.data[dataIndex]
              this.$emit('onDrilldown', {
                key: item.id,
                label: item.label,
              })
            }
          }
        })
      },
      updateChart () {
        if (this.chartInstance) {
          this.chartInstance.setOption(this.chartOptions)
        }
      },
      resizeChart () {
        if (this.chartInstance) {
          this.chartInstance.resize()
        }
      },
    },
  }
</script>

<style>
.bar-chart-container {
  width: 100%;
  height: 100%;
}
</style>
